import { CharToken, IdentifierToken, NumberToken, StringToken, type Token, UnitConversionToken } from './tokenizer.ts';
import {
  AccessField,
  Binary,
  Bool,
  CalendarValue,
  Call,
  Convert,
  type Expression,
  Group,
  Identifier,
  Negate,
  Num,
  Str,
} from './ast.ts';
import {
  type CalendarRange,
  type CalendarType,
  dateConvertToCalendarRange,
  UnitConversionError,
  UnitsTracker,
} from './units.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';

/**
 * Parses a string with a calendar unit annotation into a CalendarRange.
 *
 * Accepted input formats:
 * - 'YYYY-MM-DD' (full date, e.g. '2024-01-15')
 * - 'YYYY-MM' (year and month, e.g. '2024-01')
 * - 'YYYY' (year only, e.g. '2024')
 *
 * The calendarType controls what kind of range is produced (date, week, month, quarter, halfYear, year).
 */
export function calendarStringParse(raw: string, calendarType: CalendarType): CalendarRange | UnitConversionError {
  // Full date: YYYY-MM-DD (possibly followed by more content, e.g. a date range key)
  // No '$' anchor allows matching date-range keys like '2024-01-01-2024-03-31'
  const dateMatch = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  if (dateMatch) {
    return dateConvertToCalendarRange(dateMatch[1] as ISO8601DateOnly, calendarType);
  }

  // Year and month: YYYY-MM
  const yearMonthMatch = /^(\d{4}-\d{2})$/.exec(raw);
  if (yearMonthMatch) {
    return dateConvertToCalendarRange((yearMonthMatch[1] + '-01') as ISO8601DateOnly, calendarType);
  }

  // Year only: YYYY
  const yearMatch = /^(\d{4})$/.exec(raw);
  if (yearMatch) {
    return dateConvertToCalendarRange((yearMatch[1] + '-01-01') as ISO8601DateOnly, calendarType);
  }

  return new UnitConversionError(`Cannot parse '${raw}' as a calendar value`);
}

/**
 * Grammar:
 *
 * expression -> equality;
 * equality   -> comparison (("=" | "!=") comparison)*
 * comparison -> term (("<" | ">" | "<=" | ">=") term)*
 * term       -> factor (("+" | "-") factor)*
 * factor     -> power (("*" | "/") power)*
 * power      -> unary ("^" unary)*
 * unary      -> "-" unary
 *             | call
 * call       -> primary ( "(" arguments? ")" | "." IDENTIFIER )* UNIT_CONVERSION?
 * primary    -> NUMBER | IDENTIFIER | "(" expression ")"
 *
 * arguments  -> expression ( "," expression )*
 */

export class Parser {
  static parseExpression(input: Token[]): { error: string; index: number } | { expression: Expression } {
    const parser = new Parser(input);
    try {
      const expression = parser.expression();
      if (parser.peek().type !== 'EOF') {
        throw new Error(`Expected end of input but found: ${parser.peek().type}`);
      }
      return { expression };
    } catch (e: any) {
      return { error: e.message, index: parser.index };
    }
  }

  index = 0;
  public readonly input: Token[];
  private constructor(input: Token[]) {
    this.input = input;
  }

  expression(): Expression {
    return this.equality();
  }

  equality(): Expression {
    let comparison = this.comparison();
    while (this.match('=', '!=')) {
      comparison = new Binary(comparison, this.previous().type as '=' | '!=', this.comparison());
    }

    return comparison;
  }

  comparison(): Expression {
    let term = this.term();
    while (this.match('<', '>', '<=', '>=')) {
      term = new Binary(term, this.previous().type as '<' | '>' | '<=' | '>=', this.term());
    }

    return term;
  }

  term(): Expression {
    let factor = this.factor();
    while (this.match('+', '-')) {
      factor = new Binary(factor, this.previous().type as '+' | '-', this.factor());
    }

    return factor;
  }

  factor(): Expression {
    let power = this.power();
    while (this.match('*', '/')) {
      power = new Binary(power, this.previous().type as '*' | '/', this.power());
    }

    return power;
  }

  power(): Expression {
    const unary = this.unary();
    if (this.match('^')) {
      return new Binary(unary, '^', this.power());
    }

    return unary;
  }

  unary(): Expression {
    if (this.match('-')) {
      return new Negate(this.unary());
    }

    return this.call();
  }

  call(): Expression {
    let primary = this.primary();
    while (this.match('(', '.')) {
      if (this.previous().type === '(') {
        let args: Expression[] = [];
        if (!this.check(')')) {
          args = this.args();
        }
        this.consume(')');
        primary = new Call(primary, args);
      } else {
        const identifier = this.consume('Identifier') as IdentifierToken;
        primary = new AccessField(primary, new Identifier(identifier.name, identifier.annotation));
      }
    }

    if (this.check('UnitConversion')) {
      const token = this.advance() as UnitConversionToken;
      primary = new Convert(primary, UnitsTracker.fromUnitConversionToken(token.units));
    }

    return primary;
  }

  primary(): Expression {
    if (this.match('(')) {
      const expr = new Group(this.expression());
      this.consume(')');
      return expr;
    }

    if (this.check('Number')) {
      const token = this.advance() as NumberToken;
      return new Num(token.number, UnitsTracker.fromUnitConversionToken(token.units));
    } else if (this.check('String')) {
      const token = this.advance() as StringToken;
      if (token.units.length > 0) {
        const units = UnitsTracker.fromUnitConversionToken(token.units);
        const calendarType = units.toCalendarType();
        if (!calendarType) {
          throw new Error(`Cannot convert string to calendar value: ${token.raw} ${units.toString()}`);
        }
        const parsed = calendarStringParse(token.raw, calendarType);
        if (parsed instanceof UnitConversionError) {
          throw new Error(`Cannot parse '${token.raw}' as a calendar value. Error was: ${parsed.message}`);
        }

        return new CalendarValue(parsed);
      } else {
        return new Str(token.raw);
      }
    } else if (this.check('Identifier')) {
      const token = this.advance() as IdentifierToken;
      if (token.name === 'true') {
        return Bool.truthy;
      } else if (token.name === 'false') {
        return Bool.falsy;
      } else {
        return new Identifier(token.name, token.annotation);
      }
    }

    throw new Error(`Expected number, identifier, or group, but found: ${this.peek().type}`);
  }

  args(): Expression[] {
    const args = [this.expression()];
    while (this.match(',')) {
      args.push(this.expression());
    }
    return args;
  }

  match(...possibilities: Token['type'][]) {
    for (const type of possibilities) {
      if (this.check(type)) {
        this.advance();
        return true;
      }
    }

    return false;
  }

  check(type: Token['type']): boolean {
    if (this.isAtEnd()) {
      return false;
    }
    return this.peek().type === type;
  }

  advance(): Token {
    this.index += 1;
    return this.input[this.index - 1];
  }

  peek(): Token {
    if (this.index >= this.input.length) {
      return new CharToken('EOF', this.index);
    }

    return this.input[this.index];
  }

  previous(): Token {
    return this.input[this.index - 1];
  }

  consume(type: Token['type']): Token {
    if (this.check(type)) {
      return this.advance();
    }

    throw new Error(`Expected ${type} but found: ${this.peek().type}`);
  }

  isAtEnd(): boolean {
    return this.index >= this.input.length || this.peek().type === 'EOF';
  }
}
